import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';

export interface UserNote {
    id: string;
    type: 'note' | 'task' | 'activity';
    title: string;
    content: string;
    activity_type?: string;
    assignee_id?: string;
    due_date?: string;
    priority?: string;
    notable_type?: string | null;
    notable_id?: string | null;
    author: {
        id: string;
        name: string;
        email: string;
    };
    assignee?: {
        id: string;
        name: string;
        email: string;
    };
    notable?: {
        id: string;
        name?: string;
        email?: string;
    } | null;
    created_at: string;
    updated_at: string;
}

export interface UserNotesParams {
    page?: number;
    per_page?: number;
    search?: string;
    type?: 'note' | 'task' | 'activity';
    sort_by?: string;
    sort_direction?: 'asc' | 'desc';
}

export interface UserNotesResponse {
    data: UserNote[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

export interface BulkDeleteRequest {
    note_ids: string[];
}

export type UserNoteType = 'note' | 'task' | 'activity';

// Hook to get notes created by a specific user
export const useUserNotes = (userId: string, params?: UserNotesParams) => {
    return useQuery<UserNotesResponse>({
        queryKey: ['user-notes', userId, params],
        queryFn: async () => {
            const searchParams = new URLSearchParams();
            if (params?.page) searchParams.append('page', params.page.toString());
            if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
            if (params?.search) searchParams.append('search', params.search);
            if (params?.type) searchParams.append('type', params.type);
            if (params?.sort_by) searchParams.append('sort_by', params.sort_by);
            if (params?.sort_direction)
                searchParams.append('sort_direction', params.sort_direction);

            const url = `${API_ENDPOINTS.CRM.USER_NOTES.LIST(userId)}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
            const response = await httpClient.get(url);
            return response.data;
        },
        enabled: !!userId,
    });
};

// Hook to bulk delete notes created by a specific user
export const useBulkDeleteUserNotes = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ userId, noteIds }: { userId: string; noteIds: string[] }) => {
            console.log('Bulk delete request:', {
                userId,
                noteIds,
                endpoint: API_ENDPOINTS.CRM.USER_NOTES.BULK_DELETE(userId),
            });
            const response = await httpClient.delete(
                API_ENDPOINTS.CRM.USER_NOTES.BULK_DELETE(userId),
                {
                    data: { note_ids: noteIds },
                }
            );
            console.log('Bulk delete response:', response);
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate user notes queries
            queryClient.invalidateQueries({
                queryKey: ['user-notes', variables.userId],
                exact: false,
            });

            // Also invalidate general notes queries
            queryClient.invalidateQueries({
                queryKey: ['notes'],
                exact: false,
            });
        },
    });
};

export const useDeleteAllUserNotes = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ userId, types }: { userId: string; types?: UserNoteType[] }) => {
            const response = await httpClient.delete(
                API_ENDPOINTS.CRM.USER_NOTES.DELETE_ALL(userId),
                {
                    data: { types: types ?? ['note', 'task'] },
                }
            );
            return response.data;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['user-notes', variables.userId],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: ['notes'],
                exact: false,
            });
        },
    });
};
